import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import {
  Image as ImageIcon,
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { bannerService, StoreBanner } from '../services/banner.service'
import { BannerModal } from '../components/banners/BannerModal'
import { useSessionStore } from '../store/auth/auth.store'

export function Banners() {
  const canCreate = useSessionStore((state) => state.canAction('ecommerce.banner.create'))
  const canEdit = useSessionStore((state) => state.canAction('ecommerce.banner.edit'))
  const canChangeStatus = useSessionStore((state) => state.canAction('ecommerce.banner.status'))
  const canDelete = useSessionStore((state) => state.canAction('ecommerce.banner.delete'))
  const [banners, setBanners] = useState<StoreBanner[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')

  // Paginación
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    perPage: 10,
    lastPage: 1,
    from: 0,
    to: 0,
  })

  // Modals state
  const [showModal, setShowModal] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
  const [statusConfirmId, setStatusConfirmId] = useState<number | null>(null)
  const [editingId, setEditingId] = useState<number | null>(null)

  const fetchBanners = async (page = 1, search = searchQuery) => {
    setIsLoading(true)
    try {
      const res = await bannerService.getBanners({
        page,
        per_page: pagination.perPage,
        search,
      })
      const paginator = res.data

      setBanners(paginator.data)
      setPagination({
        currentPage: paginator.current_page,
        total: paginator.total,
        perPage: paginator.per_page,
        lastPage: paginator.last_page,
        from: paginator.from,
        to: paginator.to,
      })
    } catch (error) {
      toast.error('Error al cargar banners')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchBanners(pagination.currentPage, searchQuery)
  }, [pagination.currentPage, searchQuery])

  const openCreateModal = () => {
    setEditingId(null)
    setShowModal(true)
  }

  const openEditModal = (banner: StoreBanner) => {
    setEditingId(banner.id)
    setShowModal(true)
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingId(null)
  }

  const executeDelete = async () => {
    if (!deleteConfirmId) return
    try {
      await bannerService.deleteBanner(deleteConfirmId)
      toast.success('Banner eliminado exitosamente')
      fetchBanners()
    } catch (error) {
      toast.error('Error', { description: 'No se pudo eliminar el banner.' })
    } finally {
      setDeleteConfirmId(null)
    }
  }

  const executeChangeStatus = async () => {
    if (!statusConfirmId) return
    try {
      await bannerService.toggleBannerStatus(statusConfirmId)
      toast.success('Estado del banner actualizado exitosamente')
      fetchBanners()
    } catch (error) {
      toast.error('Error', { description: 'No se pudo actualizar el estado del banner.' })
    } finally {
      setStatusConfirmId(null)
    }
  }

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString()
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header & Filters */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Banners</h1>
          <p className="mt-1 text-sm text-slate-600">
            Administra los banners de la tienda en línea.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {canCreate && (
            <Button
              onClick={openCreateModal}
              className="w-full bg-primary hover:bg-primary/90 sm:w-auto"
            >
              <Plus className="mr-2 h-4 w-4" />
              Nuevo Banner
            </Button>
          )}
        </div>
      </div>
      <GlobalFilterBar
        show={['search']}
        values={{ search: searchQuery }}
        onChange={(changes) => {
          if (changes.search !== undefined) {
            setSearchQuery(changes.search)
            setPagination((prev) => ({ ...prev, currentPage: 1 }))
          }
        }}
        customOptions={{ searchPlaceholder: 'Buscar banner...' }}
      />

      {/* Table */}
      {isLoading ? (
        <TableSkeleton
          headers={['Nombre', 'Tipo', 'Estado', 'Periodo', 'Acciones']}
          columns={['text', 'badge', 'badge', 'text', 'actions']}
          rows={5}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                <tr className="transition-colors hover:bg-table-row-hover">
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Slides
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Periodo
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-table-header-foreground">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-table-bg text-table-foreground">
                {banners.length > 0 ? (
                  banners.map((banner) => (
                    <tr key={banner.id} className="hover:bg-table-row-hover">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <p className="font-medium text-table-foreground">{banner.name}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant="outline" className="bg-slate-100">
                          {banner.type === 'HERO' ? 'Hero' : banner.type === 'BANNER_GRID_3' ? '3 Espacios' : 'Individual'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          className={
                            banner.is_active
                              ? 'bg-[#DCFCE6] text-[#008438]'
                              : 'bg-[#FFE2E2] text-[#C10008]'
                          }
                        >
                          {banner.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-table-foreground">
                        {banner.slides?.length || 0}
                      </td>
                      <td className="px-6 py-4 text-sm text-table-foreground">
                        {banner.starts_at || banner.ends_at
                          ? `${formatDate(banner.starts_at)} - ${formatDate(banner.ends_at)}`
                          : 'Indefinido'}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canChangeStatus && (
                            <button
                              onClick={() => setStatusConfirmId(banner.id)}
                              title={banner.is_active ? 'Desactivar' : 'Activar'}
                              className={`rounded p-1.5 transition-colors ${banner.is_active? 'text-primary hover:bg-primary/10': 'text-table-foreground hover:bg-muted'}`}>{banner.is_active ? (<ToggleRight className="h-5 w-5" />) : (<ToggleLeft className="h-5 w-5" />)}</button>)}
                          {canEdit && (
                            <button
                              onClick={() => openEditModal(banner)}
                              title="Editar"
                              className="rounded p-1.5 text-table-foreground transition-colors hover:bg-muted"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => setDeleteConfirmId(banner.id)}
                              title="Eliminar"
                              className="rounded p-1.5 text-red-600 transition-colors hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <ImageIcon className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">No se encontraron banners</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {banners.length > 0 && (
            <GlobalPaginator
              totalItems={pagination.total}
              itemsPerPage={pagination.perPage}
              currentPage={pagination.currentPage}
              from={pagination.from}
              to={pagination.to}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, currentPage: page }))
              }
            />
          )}
        </div>
      )}

      {showModal && (
        <BannerModal
          open={showModal}
          onOpenChange={handleCloseModal}
          bannerId={editingId}
          onSuccess={() => {
            fetchBanners(pagination.currentPage)
            handleCloseModal()
          }}
        />
      )}

      <ConfirmationModal
        isOpen={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={executeDelete}
        preset="delete"
        itemName="banner"
      />
      <ConfirmationModal
        isOpen={statusConfirmId !== null}
        onClose={() => setStatusConfirmId(null)}
        onConfirm={executeChangeStatus}
        preset="toggleStatus"
        itemName="banner"
      />
    </div>
  )
}
